import React, { useState, useEffect, useCallback } from 'react';
import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    ModalCloseButton,
    Button,
    VStack,
    HStack,
    Text,
    Textarea,
    Badge,
    Box,
    Tabs,
    TabList,
    TabPanels,
    Tab,
    TabPanel,
    Alert,
    AlertIcon,
    useBreakpointValue
} from '@chakra-ui/react';
import { MdSave, MdPreview, MdRestore } from 'react-icons/md';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import useMarkdownComponents from './MarkdownComponents';
import { useDocuments } from './hooks/useDocuments';

const DocumentEditor = ({ isOpen, onClose, document }) => {
    const [editedText, setEditedText] = useState('');
    const [originalText, setOriginalText] = useState('');
    const [savedText, setSavedText] = useState(null); // null means "no saved edit yet"
    const [isSaving, setIsSaving] = useState(false);
    const [hasChanges, setHasChanges] = useState(false);
    const [activeTab, setActiveTab] = useState(0);

    const { getDocument, updateDocumentText } = useDocuments();
    const markdownComponents = useMarkdownComponents();
    const modalSize = useBreakpointValue({ base: "full", md: "6xl" });

    // Load document content when modal opens
    useEffect(() => {
        if (isOpen && document) {
            const loadDocument = async () => {
                try {
                    const fullDocument = await getDocument(document.id);
                    const original = fullDocument.original_text || '';
                    const edited = fullDocument.edited_text || original;

                    setOriginalText(original);
                    setEditedText(edited);
                    setSavedText(fullDocument.edited_text ?? null); // track last saved edit text
                    setHasChanges(false);
                } catch (error) {
                    console.error('Error loading document:', error);
                }
            };

            loadDocument();
        }
    }, [isOpen, document, getDocument]);

    // Track changes - compare against savedText or originalText if no saved edit exists
    useEffect(() => {
        // Only run once we have loaded at least one baseline (savedText or originalText)
        const baselineText = savedText ?? originalText;
        if (baselineText === undefined) return;

        setHasChanges(editedText !== baselineText);
    }, [editedText, originalText, savedText]);

    // Handle save
    const handleSave = useCallback(async () => {
        if (!document || !hasChanges) return;

        setIsSaving(true);
        try {
            await updateDocumentText(document.id, editedText);
            setSavedText(editedText); // this is now the new baseline
            setHasChanges(false);
        } catch (error) {
            console.error('Save failed:', error);
        } finally {
            setIsSaving(false);
        }
    }, [document, editedText, hasChanges, updateDocumentText]);

    // Reset to original OCR text
    const handleResetToOriginal = useCallback(() => {
        if (!originalText) return;
        const confirmed = window.confirm('Reset to the initial OCR result? Unsaved current edits will be lost.');
        if (!confirmed) return;
        setEditedText(originalText);
        setActiveTab(0);
    }, [originalText]);

    // Handle close with unsaved changes
    const handleClose = useCallback(() => {
        if (hasChanges) {
            const confirmed = window.confirm(
                'You have unsaved changes. Are you sure you want to close?'
            );
            if (!confirmed) return;
        }
        onClose();
    }, [hasChanges, onClose]);

    // Format file size
    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 B';
        const k = 1024;
        const sizes = ['B', 'KB', 'MB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
    };

    if (!document) return null;

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            size={modalSize}
            closeOnOverlayClick={false}
        >
            <ModalOverlay bg="blackAlpha.300" />
            <ModalContent
                maxW={{ base: "calc(100vw - 16px)", md: "90vw" }}
                maxH={{ base: "calc(100vh - 32px)", md: "90vh" }}
                minW={{ base: "calc(100vw - 16px)", md: "800px" }}
                h={{ base: "calc(100vh - 32px)", md: "auto" }}
                m={{ base: "8px", md: "auto" }}
            >
                <ModalHeader>
                    <VStack align="flex-start" spacing={2}>
                        <HStack justify="space-between" w="full">
                            <Text fontSize="lg" fontWeight="600" isTruncated>
                                {document.filename}
                            </Text>
                            {hasChanges && (
                                <Badge colorScheme="orange" size="sm">
                                    Unsaved changes
                                </Badge>
                            )}
                        </HStack>
                        <HStack fontSize="sm" color="gray.600" spacing={4}>
                            <Text>{formatFileSize(document.file_size)}</Text>
                            <Text>
                                {document.ocr_tier === 'advanced' ? 'Advanced' : 'Standard'} OCR
                            </Text>
                            <Text>
                                {editedText.length.toLocaleString()} characters
                            </Text>
                        </HStack>
                    </VStack>
                </ModalHeader>

                <ModalCloseButton />

                <ModalBody py={4} flex="1">
                    <Tabs
                        index={activeTab}
                        onChange={setActiveTab}
                        h="full"
                        display="flex"
                        flexDirection="column"
                    >
                        <TabList>
                            <Tab>Edit</Tab>
                            <Tab>Preview</Tab>
                            {savedText !== null && savedText !== originalText && (
                                <Tab>Compare</Tab>
                            )}
                        </TabList>

                        <TabPanels flex="1" overflow="hidden">
                            {/* Edit Tab */}
                            <TabPanel h="full" p={0} pt={4}>
                                <Textarea
                                    value={editedText}
                                    onChange={(e) => setEditedText(e.target.value)}
                                    placeholder="Document text will appear here..."
                                    minH={{ base: "50vh", md: "500px" }}
                                    maxH={{ base: "60vh", md: "600px" }}
                                    bg="white"
                                    fontSize="sm"
                                    fontFamily="mono"
                                    lineHeight="1.5"
                                    resize="vertical"
                                    border="1px"
                                    borderColor="gray.200"
                                    _focus={{ borderColor: "brand.500" }}
                                />
                            </TabPanel>

                            {/* Preview Tab */}
                            <TabPanel h="full" p={0} pt={4} overflow="auto">
                                <Box
                                    border="1px"
                                    borderColor="gray.200"
                                    borderRadius="md"
                                    minH={{ base: "50vh", md: "500px" }}
                                    maxH={{ base: "60vh", md: "600px" }}
                                    p={4}
                                    bg="white"
                                    overflow="auto"
                                    className="markdown-content"
                                >
                                    {editedText ? (
                                        <ReactMarkdown
                                            components={markdownComponents}
                                            remarkPlugins={[remarkGfm, remarkBreaks]}
                                            skipHtml={false}
                                            linkTarget="_blank"
                                        >
                                            {editedText}
                                        </ReactMarkdown>
                                    ) : (
                                        <Text color="gray.500" fontStyle="italic">
                                            No content to preview
                                        </Text>
                                    )}
                                </Box>
                            </TabPanel>

                            {/* Compare Tab */}
                            {savedText !== null && savedText !== originalText && (
                                <TabPanel h="full" p={0} pt={4}>
                                    <HStack spacing={4} h="full" align="stretch">
                                        <VStack flex="1" align="stretch" spacing={2}>
                                            <Text fontSize="sm" fontWeight="500" color="gray.700">
                                                Original (OCR Result)
                                            </Text>
                                            <Box
                                                border="1px"
                                                borderColor="gray.200"
                                                borderRadius="md"
                                                p={4}
                                                bg="gray.50"
                                                overflow="auto"
                                                minH={{ base: "40vh", md: "450px" }}
                                                maxH={{ base: "50vh", md: "550px" }}
                                            >
                                                <Text
                                                    fontSize="sm"
                                                    fontFamily="mono"
                                                    lineHeight="1.5"
                                                    whiteSpace="pre-wrap"
                                                >
                                                    {originalText || 'No original text available'}
                                                </Text>
                                            </Box>
                                        </VStack>

                                        <VStack flex="1" align="stretch" spacing={2}>
                                            <Text fontSize="sm" fontWeight="500" color="gray.700">
                                                Current Edit
                                            </Text>
                                            <Box
                                                border="1px"
                                                borderColor="gray.200"
                                                borderRadius="md"
                                                p={4}
                                                bg="white"
                                                overflow="auto"
                                                minH={{ base: "40vh", md: "450px" }}
                                                maxH={{ base: "50vh", md: "550px" }}
                                            >
                                                <Text
                                                    fontSize="sm"
                                                    fontFamily="mono"
                                                    lineHeight="1.5"
                                                    whiteSpace="pre-wrap"
                                                >
                                                    {editedText || 'No edited text'}
                                                </Text>
                                            </Box>
                                        </VStack>
                                    </HStack>
                                </TabPanel>
                            )}
                        </TabPanels>
                    </Tabs>
                </ModalBody>

                <ModalFooter>
                    <HStack spacing={3}>
                        <Button variant="ghost" onClick={handleClose}>
                            Close
                        </Button>
                        <Button
                            variant="outline"
                            colorScheme="orange"
                            leftIcon={<MdRestore />}
                            onClick={handleResetToOriginal}
                            isDisabled={!originalText || editedText === originalText}
                        >
                            Reset to OCR
                        </Button>
                        <Button
                            colorScheme="brand"
                            leftIcon={<MdSave />}
                            onClick={handleSave}
                            isLoading={isSaving}
                            isDisabled={!hasChanges}
                            loadingText="Saving..."
                        >
                            Save Changes
                        </Button>
                    </HStack>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export default DocumentEditor;